import { Order } from '@castlery/modules-user-domain';
import * as React from 'react';
import { toPrice } from '@castlery/utils';
import { EcEnv } from '@castlery/config';
import { Grid } from '@castlery/fortress';
import { SalesOrderCell } from './sales-order-cell';
import { Box, Typography, TypographySystem } from '@castlery/fortress';
import { orderFeatureService } from '@castlery/modules-order-services';

interface DetailsFooterProps {
  order: Order;
  sx?: any;
}

export const DetailsFooter = (props: DetailsFooterProps) => {
  const { order, sx } = props;
  const taxAddonDescription = orderFeatureService.getOrderSummaryTotalAddonDescription();
  const showSalesTax = orderFeatureService.showSalesTax;
  const localCountry = EcEnv.NEXT_PUBLIC_COUNTRY.toLocaleLowerCase();
  const footerDisplay = React.useMemo(() => {
    return {
      '& .MuiBox-root': {
        'justify-content': 'space-between',
      },
    };
  }, []);
  const coupon = React.useMemo(() => {
    return order.coupon ? +order.coupon.amount : 0;
  }, [order.coupon]);
  const manualDiscountTotal = React.useMemo(() => {
    return order.line_items.reduce((result, item) => {
      return result + (+item.manual_discount_total || 0);
    }, 0);
  }, [order]);

  const promoTotal = React.useMemo(() => {
    if (localCountry === 'us') {
      const shipmentPromo = order.promotions.find((p) => p.adjustable_type === 'shipment');
      if (shipmentPromo) {
        return +order.promo_total - +shipmentPromo.amount;
      } else {
        return +order.promo_total;
      }
    } else {
      return +order.adjustment_total - coupon;
    }
  }, [coupon, localCountry, order.adjustment_total, order.promo_total, order.promotions]);

  const servicesTotal = React.useMemo(() => {
    return +order.service_fee_total || 0; //service fee
  }, [order]);
  const taxTotal = React.useMemo(() => {
    return +order.tax_total; //tax
  }, [order]);
  const footerConfig = React.useMemo(() => {
    return [
      {
        cellInfo: {
          label: 'Items Subtotal',
          value: `${toPrice(+order.item_total + manualDiscountTotal)}`,
        },
        xs: 12,
        align: 'row',
        sx: footerDisplay,
      },
      {
        cellInfo: {
          label: 'Warranty Subtotal',
          value: +order.warranty_total > 0 ? `${toPrice(+order.warranty_total)}` : '',
        },
        xs: 12,
        align: 'row',
        sx: footerDisplay,
      },
      {
        cellInfo: {
          label: 'Shipping',
          value: `${toPrice(+order.shipment_total - servicesTotal)}`,
        },
        xs: 12,
        align: 'row',
        sx: footerDisplay,
      },
      {
        cellInfo: {
          label: 'Services',
          value: servicesTotal ? `${toPrice(servicesTotal)}` : '',
        },
        xs: 12,
        align: 'row',
        sx: footerDisplay,
      },
      {
        cellInfo: {
          label: 'Promotion',
          value: promoTotal ? `${toPrice(promoTotal - manualDiscountTotal)}` : '',
        },
        xs: 12,
        align: 'row',
        sx: footerDisplay,
      },
      {
        cellInfo: {
          label:
            localCountry !== 'us' && order.coupon ? (
              <Box>
                <Typography
                  level={'title-md' as keyof TypographySystem | 'inherit'}
                  sx={{
                    fontSize: '1rem',
                    fontWeight: 600,
                    marginRight: '0.5rem',
                    display: 'inline-block',
                  }}
                >
                  Coupon Code:
                </Typography>
                <strong
                  style={{
                    color: '#d23b3f',
                  }}
                >
                  {order.coupon.code.toUpperCase()}
                </strong>
              </Box>
            ) : (
              ''
            ),
          renderOptions:
            localCountry !== 'us' && order.coupon ? (
              <Typography
                level={'body-sm' as keyof TypographySystem | 'inherit'}
                sx={{
                  color: '#666',
                  fontSize: '1rem',
                }}
              >
                {toPrice(order.coupon.amount)}
              </Typography>
            ) : undefined,
        },
        xs: 12,
        align: 'row',
        sx: footerDisplay,
      },
      ...(showSalesTax
        ? [
            {
              cellInfo: {
                label: 'Sales Tax',
                value: taxTotal ? `${toPrice(taxTotal)}` : '',
              },
              xs: 12,
              align: 'row',
              sx: footerDisplay,
            },
          ]
        : []),
      {
        cellInfo: {
          label: (
            <Box
              sx={{
                color: 'var(--fortress-palette-text-primary, var(--fortress-palette-neutral-800, #171A1C))',
              }}
            >
              <span
                style={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  marginRight: '0.5rem',
                }}
              >
                Total
              </span>
              <em
                style={{
                  color: '#888',
                  fontWeight: 'normal',
                  fontSize: '0.8rem',
                  fontStyle: 'normal',
                }}
              >
                ({taxAddonDescription})
              </em>
            </Box>
          ),
          value: `${toPrice(+order.total)}`,
        },
        xs: 12,
        align: 'row',
        sx: {
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '1px',
            backgroundColor: '#eee',
          },
          '& p': {
            color: '#424242',
            fontWeight: 600,
            fontSize: '1.2rem',
          },
          ...footerDisplay,
        },
      },
    ];
  }, [
    footerDisplay,
    localCountry,
    manualDiscountTotal,
    order.coupon,
    order.item_total,
    order.shipment_total,
    order.total,
    order.warranty_total,
    promoTotal,
    servicesTotal,
    taxTotal,
    showSalesTax,
    taxAddonDescription,
  ]);

  return (
    <Grid sx={sx} container rowSpacing={1}>
      {footerConfig.map((cell) =>
        cell.cellInfo.value || cell.cellInfo.renderOptions ? <SalesOrderCell {...cell} /> : ''
      )}
    </Grid>
  );
};
